import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { SparkLinkedService, sparkAuthTypes } from 'app/model/linkedservice/spark.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SparkLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                port: '*',
                serverType: '*',
                thriftTransportProtocol: '*',
                authenticationType: '*',
                username: '*',
                password: 'secret',
                httpPath: '*',
                enableSsl: '*',
                trustedCertPath: '*',
                useSystemTrustStore: '*',
                allowHostNameCNMismatch: '*',
                allowSelfSignedServerCert: '*',
                encryptedCredential: '*',
                enableServerCertificateValidation: '*'
            }
        }
    }
})
export class SparkLinkedServiceAdapter extends LinkedServiceAdapter<SparkLinkedService> {
    protected modelConstructor(): new () => SparkLinkedService {
        return SparkLinkedService;
    }

    protected model(): IModel {
        return SparkLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SparkLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SparkLinkedService;
        properties.type = Metamodel.modelFor(SparkLinkedService).name;
        const password: Model.SecretBase = (linkedService.authenticationType === sparkAuthTypes.UsernameAndPassword || linkedService.authenticationType === sparkAuthTypes.WindowsAzureHDInsightService)
            && passwordOrAKVtoSecretBase(linkedService.password, this.context);
        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                enableServerCertificateValidation: linkedService.enableSsl ? linkedService.enableServerCertificateValidation : undefined,
                password
            };
        } else {
            properties.typeProperties = {
                ...this._commonProperties(linkedService),
                password,
                trustedCertPath: linkedService.enableSsl && linkedService.trustedCertPath ? linkedService.trustedCertPath : undefined,
                useSystemTrustStore: linkedService.enableSsl ? linkedService.useSystemTrustStore : undefined,
                allowHostNameCNMismatch: linkedService.enableSsl ? linkedService.allowHostNameCNMismatch : undefined,
                allowSelfSignedServerCert: linkedService.enableSsl ? linkedService.allowSelfSignedServerCert : undefined
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SparkLinkedService): Promise<SparkLinkedService> {
        const properties = linkedServiceResource.properties as Model.SparkLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SparkLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        const isV2 = properties.version === CONNECTOR_VERSION20;
        linkedService.isLegacyConnector = !isV2;
        linkedService.host = typeProperties.host;
        linkedService.port = typeProperties.port;
        linkedService.serverType = typeProperties.serverType;
        linkedService.thriftTransportProtocol = typeProperties.thriftTransportProtocol;
        linkedService.authenticationType = typeProperties.authenticationType;
        linkedService.username = typeProperties.username;
        linkedService.password = secretBaseToPasswordOrAKV(
            typeProperties.password,
            typeProperties.authenticationType === sparkAuthTypes.UsernameAndPassword || typeProperties.authenticationType === sparkAuthTypes.WindowsAzureHDInsightService, this.context
        );
        linkedService.httpPath = typeProperties.httpPath;
        linkedService.enableSsl = typeProperties.enableSsl;
        // v2
        linkedService.enableServerCertificateValidation = isV2 ? typeProperties.enableServerCertificateValidation : undefined;
        // v1
        linkedService.trustedCertPath = isV2 ? undefined : typeProperties.trustedCertPath;
        linkedService.useSystemTrustStore = isV2 ? undefined : typeProperties.useSystemTrustStore;
        linkedService.allowHostNameCNMismatch = isV2 ? undefined : typeProperties.allowHostNameCNMismatch;
        linkedService.allowSelfSignedServerCert = isV2 ? undefined : typeProperties.allowSelfSignedServerCert;
        return linkedService;
    }
    private _commonProperties(linkedService: SparkLinkedService): Model.SparkLinkedServiceTypeProperties {
        return {
            host: linkedService.host,
            port: Number(linkedService.port),
            serverType: linkedService.serverType,
            thriftTransportProtocol: linkedService.thriftTransportProtocol,
            authenticationType: linkedService.authenticationType,
            username: linkedService.authenticationType !== sparkAuthTypes.Anonymous ? linkedService.username : undefined,
            httpPath: linkedService.httpPath ? linkedService.httpPath : undefined,
            enableSsl: linkedService.enableSsl
        };
    }
}
